import React, { useState, useContext, useEffect } from 'react';
import { Menu, MenuItem, Box } from '@mui/material';
import { useRecursiveColorChange } from 'hooks/codes/mutations/useUpdateCode';
import ProjectContext from 'contexts/ProjectContext';
import { CODE_COLORS } from 'constants/CodeColorConstants';



/**
 * Color picker menu for a code, anchored to the code card's color swatch
 * (`#color-button-{codeId}`, rendered by CodeLabelBody).
 *
 * Two ways to drive it:
 * - Uncontrolled: pass a child element; clicking it opens the menu. This is
 *   how CodeLabelBody wraps the swatch itself.
 * - Controlled: pass `open` / `onClose` and no child. Use this when the
 *   trigger lives somewhere that unmounts on click — e.g. an item inside a
 *   Base UI dropdown popup, which is torn down the moment an item is chosen
 *   (GlyphDropdown's "Change Color"). Wrapping the item would unmount this
 *   menu along with the popup before it could open.
 *
 * @param {Object} props
 * @param {string} props.codeId - The ID of the code
 * @param {boolean} [props.open] - Controlled open state
 * @param {() => void} [props.onClose] - Called whenever the menu wants to
 *   close (color picked, backdrop click, Escape, or no swatch to anchor to)
 * @param {boolean} [props.isVisible] - Parent override; false forces the menu closed
 * @param {React.ReactElement} [props.children] - Uncontrolled trigger element
 */
const ColorSelector = ({
  codeId,
  open: controlledOpen,
  onClose,
  // allows the parent to override the visibility of the menu
  isVisible = true,
  children
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl) && isVisible;
  const projectContext = useContext(ProjectContext);
  const projectId = projectContext?.project?.id;
  const { handleColorChange } = useRecursiveColorChange(projectId, codeId);

  const findAnchor = () => document.getElementById(`color-button-${codeId}`);

  // Controlled mode: mirror `open` into the anchor. The swatch is looked up
  // at open time because another component renders it.
  useEffect(() => {
    if (controlledOpen === undefined) return;
    const element = controlledOpen ? findAnchor() : null;
    setAnchorEl(element);
    if (controlledOpen && !element && onClose) {
      // Nothing to anchor to (e.g. card in merge mode); hand the open state back
      // so the parent doesn't stay stuck on `open`.
      onClose();
    }
  }, [controlledOpen, codeId]);

  const handleClick = () => {
    const element = findAnchor();
    if (element) {
      setAnchorEl(element);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    if (onClose) onClose();
  };

  const handleColorSelect = (color) => {
    handleColorChange(color);
    handleClose();
  };

  return (
    <>
      {children && React.cloneElement(children, {
        onClick: (event) => {
          // Call the original onClick if it exists
          if (children.props.onClick) {
            children.props.onClick(event);
          }
          // Then handle our click
          handleClick(event);
        },
        style: {
          cursor: 'pointer',
          ...(children.props.style || {})
        }
      })}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 200
            }
          }
        }}
      >
        <MenuItem disabled sx={{
          padding: '8px 16px',
          fontWeight: 'normal',
          fontSize: '14px',
          color: 'black',
          cursor: 'default'
        }}>
          Change code color
        </MenuItem>
        {Object.entries(CODE_COLORS).map(([name, color]) => (
          <MenuItem
            key={name}
            onClick={() => handleColorSelect(name)}
            sx={{
              padding: '8px 4',
              minHeight: 'auto',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                width: 20,
                height: 20,
                backgroundColor: color,
                borderRadius: '50%',
                marginRight: 1.5,
                flexShrink: 0
              }}
            />
            <Box sx={{ lineHeight: 1 }}
>
              {name.charAt(0).toUpperCase() + name.slice(1)}
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ColorSelector;